/**
 * Identidad de acceso: el trabajador de campo entra con su DOCUMENTO, no con un correo.
 *
 * Los accesos de campo se crean en Supabase Auth con un correo sintético
 * `<documento>@campo.treid.pe`. Nadie usa ese buzón: es solo el identificador interno.
 * El personal de oficina sí entra con su correo real (npatino@treid.pe).
 */

/** Dominio interno de los accesos de campo. */
export const CAMPO_EMAIL_DOMAIN = 'campo.treid.pe';

/** Adornos que la gente escribe en un documento: espacios, puntos y guiones (de todo tipo). */
const SEPARADORES = /[\s.\-‐-―]+/g;

/**
 * Normaliza un documento: quita espacios, puntos, guiones y CEROS A LA IZQUIERDA.
 * El usuario puede escribir 02086353 o 2086353 y debe entrar igual: los Excel de RR.HH.
 * pierden el cero inicial al abrirse, así que ambas formas circulan en la empresa.
 * Un valor de puros ceros conserva un "0": normalizar nunca vacía lo que traía algo.
 */
export function normalizarDocumento(valor: string): string {
  return valor.replace(SEPARADORES, '').replace(/^0+(?=.)/, '');
}

/** true si lo escrito parece un documento y no un correo. */
export function pareceDocumento(entrada: string): boolean {
  if (entrada.includes('@')) return false;
  return /^\d+$/.test(normalizarDocumento(entrada));
}

/**
 * Un USUARIO de oficina sin documento (0394; por ejemplo `jperez`): empieza con letra, sin espacios ni
 * arroba. Su cuenta es `<usuario>@campo.treid.pe`, como la del documento. Se exige que empiece con letra para que un
 * documento mal tipeado («77013910a») no se lea como usuario, y sin espacios para que quien escribe su nombre
 * («Ana Quispe») siga viendo el aviso de que se entra con el documento.
 */
export function pareceUsuario(entrada: string): boolean {
  return /^[a-z][a-z0-9._-]*$/i.test(entrada.trim());
}

/**
 * true si lo escrito es un correo de FUERA del dominio de campo — o sea, el correo corporativo
 * real de alguien de oficina, no el sintético con el que se autentica la cuenta.
 *
 * Solo en ese caso vale la pena el segundo intento contra `login-oficina`: un documento y un
 * `@campo.treid.pe` ya se probaron directo contra Supabase, y repetirlos gastaría el límite de
 * intentos al doble de velocidad sin cambiar el resultado.
 */
export function esCorreoDeFuera(entrada: string): boolean {
  const texto = entrada.replace(/\s+/g, '').toLowerCase();
  const arroba = texto.indexOf('@');
  if (arroba <= 0) return false;
  return !texto.endsWith(`@${CAMPO_EMAIL_DOMAIN}`);
}

/**
 * Convierte lo que el usuario escribió en el correo con el que se autentica.
 * Si parece documento (solo dígitos tras normalizar) → `<doc>@campo.treid.pe`.
 * Si parece correo (tiene @) → el correo tal cual, en minúsculas y sin espacios.
 */
export function resolverCorreoDeAcceso(entrada: string): string {
  return candidatosCorreoDeAcceso(entrada)[0] ?? '';
}

/**
 * Correos a probar, en orden, para lo que el usuario escribió.
 *
 * Casi siempre es uno solo. La excepción es el documento con cero a la izquierda: el acceso
 * pudo haberse creado desde el Excel de RR.HH. (que perdió el cero) o desde el DNI físico
 * (que lo trae), y desde el navegador no hay forma de saber cuál de los dos existe. Se prueba
 * primero el normalizado y, si las credenciales fallan, el crudo. Sin ese segundo intento,
 * quien tiene cero inicial no entraría con NINGUNA de las dos formas.
 */
export function candidatosCorreoDeAcceso(entrada: string): string[] {
  // Los espacios se van siempre; los puntos NO, que un correo los necesita.
  const texto = entrada.replace(/\s+/g, '').toLowerCase();
  if (texto.length === 0) return [];
  if (texto.includes('@')) return [texto];
  if (pareceUsuario(entrada)) return [`${texto}@${CAMPO_EMAIL_DOMAIN}`];

  const crudo = entrada.replace(SEPARADORES, '');
  if (!/^\d+$/.test(crudo)) return [texto];

  const normalizado = normalizarDocumento(crudo);
  const correos = [`${normalizado}@${CAMPO_EMAIL_DOMAIN}`];
  if (crudo !== normalizado) correos.push(`${crudo}@${CAMPO_EMAIL_DOMAIN}`);
  return correos;
}
